import React from 'react';
import { Text } from '@uhg-abyss/web/ui/Text';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { styled } from '@uhg-abyss/web/tools/styled';
import type { QuestionOption, SurveyAssignedQuestion, BranchRuleInput } from './types';

interface BranchingOptionRowProps {
  option: QuestionOption;
  eligibleTargets: SurveyAssignedQuestion[];
  currentJumpToQuestionId: number | null;
  onChange: (rule: BranchRuleInput | null) => void;
}

const Row = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr auto',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.5rem 0',
  borderBottom: '1px solid #e0e8f0',
});

const OptionLabel = styled(Text, {
  margin: 0,
  fontWeight: 500,
});

const selectInputCss = {
  'abyss-select-input-root': {
    width: '100%',
    minWidth: '12rem',
  },
  'abyss-select-input-label': {
    color: '$web.semantic.color.text.content.primary',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  'abyss-select-input-container': {
    minHeight: '2.5rem',
    borderRadius: '8px',
    border: '1px solid #b6c9d9',
    boxSizing: 'border-box' as const,
  },
  'abyss-select-input-selection-container': {
    minHeight: '2.5rem',
    padding: '0 0.75rem',
    fontSize: '0.95rem',
  },
} as const;

export const BranchingOptionRow = ({
  option,
  eligibleTargets,
  currentJumpToQuestionId,
  onChange,
}: BranchingOptionRowProps) => {
  const handleChange = (value: string | number | Function) => {
    const val = String(value ?? '');
    if (!val) {
      onChange(null);
    } else {
      onChange({ questionOptionId: option.questionOptionId, jumpToQuestionId: parseInt(val, 10) });
    }
  };

  return (
    <Row>
      <OptionLabel aria-label={`Option: ${option.optionTitle}`}>{option.optionTitle}</OptionLabel>
      <SelectInput
        label={`Jump target for option ${option.optionTitle}`}
        hideLabel
        isClearable
        css={selectInputCss}
        options={eligibleTargets.map((target) => {return {
          label: `${target.orderId !== null ? `${target.orderId}. ` : ''}${target.questionTitle}`,
          value: target.questionId,
        }})}
        value={currentJumpToQuestionId ?? ''}
        onChange={handleChange}
        placeholder="No jump (continue)"
        aria-label={`Jump target for option ${option.optionTitle}`}
      />
    </Row>
  );
};
